import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Alert,
  AlertIcon,
  Badge,
  Spinner,
  useToast,
  Select,
  useBreakpointValue,
  Button,
  Flex,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import axios from "axios";
import PaymentModal from "./Modal";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const PaidList = () => {
  const [feesData, setFeesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const toast = useToast();
  const userId = sessionStorage.getItem("user_id");
  const [selectedFee, setSelectedFee] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    if (!userId) {
      setError("User ID not found in session. Please log in.");
      return;
    }
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [paidFeesRes, unpaidFeesRes] = await Promise.all([
        axios.post(`${BASE_URL}/guardian/fees/paid.php`, {
          user_id: userId,
        }),
        axios.post(`${BASE_URL}/guardian/fees/fees_view.php`, {
          user_id: userId,
        }),
      ]);

      const paidFees = paidFeesRes.data.success
        ? paidFeesRes.data.data.flatMap((student) =>
            student.paid_fees.map((fee) => ({
              ...fee,
              type: "paid",
            }))
          )
        : [];

      const unpaidFees = unpaidFeesRes.data.success
        ? unpaidFeesRes.data.data.map((fee) => ({
            ...fee,
            type: "unpaid",
          }))
        : [];
      console.log("mkm", unpaidFees);

      setFeesData([...unpaidFees, ...paidFees]);
    } catch (err) {
      setError("Failed to fetch fees data");
      toast({
        title: "Error",
        description: "Unable to fetch fees data.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePay = (fee) => {
    // Implement your payment logic here
    toast({
      title: "Payment initiated",
      description: `Payment for ${fee.fee_name} initiated.`,
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };
  const selectWidth = useBreakpointValue({ base: "100%", md: "100px" });

  const filteredFees = feesData.filter((fee) =>
    filter === "all" ? true : fee.type === filter
  );

  const totalPages = Math.ceil(filteredFees.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const paginatedFees = filteredFees.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
      >
        <Spinner size="xl" color="teal.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const handlePayment = (amount) => {
    console.log(`Paid ${amount} for fee: ${selectedFee.fee_name}`);
    // Additional logic to process the payment (e.g., API call)
  };

  return (
    <Box w="100%" p={1}>
      <Flex
        justify="space-between"
        p={3}
        className="sb"
        align="center"
        mb={1}
        direction={{ base: "column", md: "row" }} // Responsive direction
        gap={3} // Adds spacing between items
      >
        <Select
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          w={selectWidth}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={15}>15</option>
        </Select>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          w="200px"
        >
          <option value="all">All Fees</option>
          <option value="paid">Paid Fees</option>
          <option value="unpaid">Unpaid Fees</option>
        </Select>
      </Flex>

      {filteredFees.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          No fees found.
        </Alert>
      ) : (
        <Box overflowX="auto">
          <Table>
            <Thead>
              <Tr>
                <Th>Fee Name</Th>
                <Th>Type</Th>
                <Th>Payment Date</Th>
                <Th>Due Date</Th>
                <Th>Amount</Th>
                <Th>Fine</Th>
                <Th>Discount</Th>
                <Th>Total</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedFees.map((fee, index) => (
                <Tr key={index}>
                  <Td>{fee.fee_name}</Td>
                  <Td>
                    <Badge colorScheme={fee.type === "paid" ? "green" : "red"}>
                      {fee.type === "paid" ? "Paid" : "Unpaid"}
                    </Badge>
                  </Td>
                  <Td>{formatDate(fee.payment_date || "-")}</Td>
                  <Td>{formatDate(fee.due_date)}</Td>
                  <Td>{formatAmount(fee.total_amount || 0)}</Td>
                  <Td>
                    {fee.fine_amount > 0 ? formatAmount(fee.fine_amount) : "-"}
                  </Td>
                  <Td>{fee.discount > 0 ? formatAmount(fee.discount) : "-"}</Td>
                  <Td>{formatAmount(fee.t_total || 0)}</Td>
                  <Td>
                    {fee.type === "unpaid" && (
                      <Button
                        colorScheme="teal"
                        size="sm"
                        onClick={() => {
                          setSelectedFee(fee);
                          onOpen();
                        }}
                      >
                        Pay
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Flex justify="end" className="sb2" borderTopWidth="1px" mt={1} p={3}>
        <Button
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>
        <Stack direction="row" mx={2}>
          {currentPage > 1 && (
            <>
              <Button onClick={() => handlePageChange(1)}>1</Button>
              {currentPage > 2 && <Text>...</Text>}
            </>
          )}
          <Button backgroundColor="teal" color="white">
            {currentPage}
          </Button>
          {currentPage < totalPages && (
            <>
              <Text>...</Text>
              <Button onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </Button>
            </>
          )}
        </Stack>
        <Button
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      <PaymentModal
        isOpen={isOpen}
        onClose={onClose}
        feeName={selectedFee ? selectedFee.fee_name : ""}
        amount={selectedFee ? selectedFee.t_total : ""}
        last={selectedFee ? selectedFee.due_date : ""}
        feeMasterId={selectedFee ? selectedFee.fee_master_id : ""}
        status={selectedFee ? selectedFee.payment_status : ""}
        userId={selectedFee ? selectedFee.user_id : ""}
        stuId={selectedFee ? selectedFee.student_id : ""}
        studentledid={selectedFee ? selectedFee.Ldg_id : ""}
        onPay={handlePayment}
      />
    </Box>
  );
};

export default PaidList;
